import { joinApi } from '@/lib/api';
import { clearSession, getSession, setSession } from '@/lib/session';

export type LoginResponse = {
  token: string;
  message?: string;
  role?: string;
  user?: {
    id?: string;
    email?: string;
    role?: string;
    name?: string | null;
    almacen_id?: number | null;
    isInternal?: boolean;
    is_internal?: boolean;
  };
  [key: string]: unknown;
};

export async function login(email: string, password: string): Promise<LoginResponse> {
  const response = await fetch(joinApi('/auth/login'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  let json: LoginResponse | null = null;
  try {
    json = (await response.json()) as LoginResponse;
  } catch {
    json = null;
  }

  if (!response.ok || !json?.token) {
    clearSession();
    throw new Error(json?.message || 'Login failed');
  }

  const user = json.user;

  setSession(json.token, {
    id: user?.id ?? email,
    email: user?.email ?? email,
    role: user?.role ?? json.role ?? '',
    name: user?.name ?? user?.email ?? email,
    almacen_id: (user as any)?.almacen_id ?? null,
    isInternal: user?.isInternal ?? user?.is_internal ?? true,
  });

  return json;
}

export function logout(): void {
  clearSession();
}

export type ClientInvitationDetails = {
  invitationId: string;
  name: string;
  email: string;
  expiresAt: string;
};

async function clientInvitationRequest<T>(path: string, body: Record<string, unknown>): Promise<T> {
  const response = await fetch(joinApi(path), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const json = await response.json().catch(() => null) as { ok?: boolean; data?: T; message?: string; code?: string } | null;
  if (!response.ok || !json?.ok || json.data === undefined) {
    const error = new Error(json?.message || 'No se pudo validar la invitación.') as Error & { code?: string };
    error.code = json?.code;
    throw error;
  }
  return json.data;
}

export function validateClientInvitation(token: string) {
  return clientInvitationRequest<ClientInvitationDetails>('/auth/client-invitations/validate', { token });
}

export function acceptClientInvitation(token: string, password: string) {
  return clientInvitationRequest<{ name: string; email: string }>('/auth/client-invitations/accept', { token, password });
}

type PasswordResetResponse<T = undefined> = {
  ok?: boolean;
  data?: T;
  message?: string;
  code?: string;
};

async function passwordResetRequest<T = undefined>(path: string, body: Record<string, unknown>): Promise<PasswordResetResponse<T>> {
  const response = await fetch(joinApi(path), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const json = await response.json().catch(() => null) as PasswordResetResponse<T> | null;
  if (!response.ok || !json?.ok) {
    const error = new Error(json?.message || 'No pudimos completar la solicitud. Intenta nuevamente.') as Error & {
      code?: string;
      status?: number;
    };
    error.code = json?.code;
    error.status = response.status;
    throw error;
  }
  return json;
}

export function requestPasswordReset(email: string) {
  return passwordResetRequest('/auth/forgot-password', { email });
}

export function resetPassword(token: string, password: string) {
  return passwordResetRequest<{ email: string; name: string }>('/auth/reset-password', { token, password });
}

export async function refreshStoredSession(options: { timeoutMs?: number } = {}): Promise<boolean> {
  const session = getSession();
  if (!session.token) return false;

  const controller = new AbortController();
  const timeoutId = globalThis.setTimeout(
    () => controller.abort(),
    Math.max(250, options.timeoutMs ?? 4_500),
  );

  try {
    const response = await fetch(joinApi('/auth/refresh'), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${session.token}`,
      },
      cache: 'no-store',
      signal: controller.signal,
    });
    const json = (await response.json().catch(() => null)) as LoginResponse | null;
    if (!response.ok || !json?.token || !json.user) {
      if (response.status === 401 || response.status === 403) {
        clearSession();
      }
      return false;
    }

    const user = json.user;
    setSession(json.token, {
      id: user.id ?? user.email ?? session.user?.id ?? '',
      email: user.email ?? session.user?.email ?? '',
      role: user.role ?? session.user?.role ?? '',
      name: user.name ?? user.email ?? session.user?.name ?? '',
      almacen_id: user.almacen_id ?? session.user?.almacen_id ?? null,
      isInternal: user.isInternal ?? user.is_internal ?? session.user?.isInternal ?? true,
    });
    return true;
  } catch {
    // Preserve the stored session during timeouts, offline mode or transient API failures.
    // A definitive 401/403 response still clears it above.
    return false;
  } finally {
    globalThis.clearTimeout(timeoutId);
  }
}
